import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Surface } from "@/components/app/Primitives";
import PushToggle from "@/components/app/PushToggle";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound } from "lucide-react";

export default function MyProfile() {
    const { user } = useAuth();
    const [emp, setEmp] = useState(null);
    const [form, setForm] = useState({ phone: "", address: "" });
    useEffect(() => {
        api.get("/me/profile").then((r) => { setEmp(r.data); setForm({ phone: r.data.phone || "", address: r.data.address || "" }); }).catch(() => setEmp({}));
    }, []);
    const save = async () => {
        try { await api.put("/me/profile", form); toast.success("Profile updated"); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };
    if (!emp) return null;
    return (
        <div>
            <PageHeader title="My Profile" subtitle="Personal information on file. Contact HR to update employment, salary, or government IDs." />

            <div className="mb-3"><PushToggle /></div>
            <Surface className="p-5 grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div><Label>Employee No.</Label><Input value={emp.employee_no || "—"} disabled /></div>
                <div><Label>Name</Label><Input value={`${emp.first_name || ""} ${emp.last_name || ""}`} disabled /></div>
                <div><Label>Position</Label><Input value={emp.position || "—"} disabled /></div>
                <div><Label>Email</Label><Input value={emp.email || user?.email || ""} disabled /></div>
                <div><Label>Hire Date</Label><Input value={emp.hire_date || "—"} disabled /></div>
                <div><Label>Status</Label><Input value={emp.employment_status || "—"} disabled /></div>
                <div className="col-span-1"><Label>Phone (you can edit)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="my-phone" /></div>
                <div className="col-span-2"><Label>Address (you can edit)</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="my-address" /></div>
            </Surface>
            <div className="mt-3 flex justify-end">
                <button onClick={save} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium" data-testid="save-my-profile">Save changes</button>
            </div>

            <MfaSecurity />
        </div>
    );
}

function MfaSecurity() {
    const [status, setStatus] = useState(null);      // {enabled, recovery_codes_remaining}
    const [phase, setPhase] = useState("idle");       // idle | setup | recovery | disable
    const [setup, setSetup] = useState(null);         // {secret, qr}
    const [code, setCode] = useState("");
    const [recovery, setRecovery] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
    useEffect(() => { load(); }, []);

    const beginSetup = async () => {
        setBusy(true);
        try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); setPhase("setup"); setCode(""); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };
    const enable = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
            setRecovery(data.recovery_codes || []); setPhase("recovery"); setSetup(null); setCode("");
            toast.success("Two-factor authentication is on");
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };
    const disable = async () => {
        setBusy(true);
        try {
            await api.post("/auth/mfa/disable", { code: code.trim() });
            setPhase("idle"); setCode(""); toast.success("Two-factor authentication turned off"); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    if (!status) return null;
    const on = status.enabled;

    return (
        <Surface className="p-5 mt-5" data-testid="mfa-security">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    <div className={`size-10 rounded-md grid place-items-center ${on ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                        {on ? <ShieldCheck className="size-5" /> : <ShieldOff className="size-5" />}
                    </div>
                    <div>
                        <div className="font-display font-semibold">Two-Factor Authentication</div>
                        <div className="text-sm text-muted-foreground max-w-lg">
                            {on
                                ? <>On — you’ll enter a code from your authenticator app each time you sign in. <b>{status.recovery_codes_remaining}</b> backup codes remaining.</>
                                : <>Add a second step at sign-in using an authenticator app (Google Authenticator, Authy, 1Password…). Recommended for HR, Payroll, and Accounting roles.</>}
                        </div>
                    </div>
                </div>
                <div>
                    {!on && phase === "idle" && <button onClick={beginSetup} disabled={busy} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium" data-testid="mfa-setup-btn">Set up</button>}
                    {on && phase === "idle" && <button onClick={() => { setPhase("disable"); setCode(""); }} className="h-9 px-3 rounded-md border border-destructive/40 text-destructive text-sm font-medium" data-testid="mfa-disable-btn">Turn off</button>}
                </div>
            </div>

            {phase === "setup" && setup && (
                <div className="mt-5 border-t pt-5 grid md:grid-cols-[180px_1fr] gap-5 items-start">
                    <div>
                        <img src={setup.qr} alt="Scan this QR code" className="w-[180px] h-[180px] rounded-md border bg-white p-2" />
                    </div>
                    <div>
                        <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1">
                            <li>Scan the QR code with your authenticator app.</li>
                            <li>Or enter this key manually: <code className="font-mono text-foreground break-all">{setup.secret}</code></li>
                            <li>Enter the 6-digit code it shows:</li>
                        </ol>
                        <div className="flex items-center gap-2 mt-3 max-w-xs">
                            <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" className="text-center tracking-[0.3em] font-mono" data-testid="mfa-enable-code" />
                            <button onClick={enable} disabled={busy || code.trim().length < 6} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium whitespace-nowrap" data-testid="mfa-enable-btn">Verify &amp; enable</button>
                        </div>
                        <button onClick={() => { setPhase("idle"); setSetup(null); }} className="mt-2 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                </div>
            )}

            {phase === "recovery" && (
                <div className="mt-5 border-t pt-5">
                    <div className="flex items-center gap-2 mb-2"><KeyRound className="size-4 text-amber-600" /><b className="text-sm">Save your backup codes</b></div>
                    <p className="text-sm text-muted-foreground mb-3">Store these somewhere safe. Each code works once if you lose your authenticator. They won’t be shown again.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg">
                        {recovery.map((c) => <code key={c} className="font-mono text-sm bg-muted rounded px-2 py-1 text-center">{c}</code>)}
                    </div>
                    <button onClick={() => { navigator.clipboard?.writeText(recovery.join("\n")); toast.success("Copied"); }} className="mt-3 h-8 px-3 rounded-md border text-sm" data-testid="mfa-copy-recovery">Copy codes</button>
                    <button onClick={() => setPhase("idle")} className="mt-3 ml-2 h-8 px-3 rounded-md bg-primary text-white text-sm">Done</button>
                </div>
            )}

            {phase === "disable" && (
                <div className="mt-5 border-t pt-5 max-w-sm">
                    <Label>Enter a current code to turn off</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456 or backup code" className="font-mono" data-testid="mfa-disable-code" />
                        <button onClick={disable} disabled={busy || !code.trim()} className="h-9 px-3 rounded-md border border-destructive/40 text-destructive text-sm font-medium whitespace-nowrap">Turn off</button>
                    </div>
                    <button onClick={() => { setPhase("idle"); setCode(""); }} className="mt-2 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                </div>
            )}
        </Surface>
    );
}
